import { TextField as RHFTextField } from "$components/rhf-fields";
import { styled, type Theme } from "@material-ui/core";
import type { ComponentProps } from "react";
import {
  InvoiceCardSection,
  useInvoiceCardLayout,
} from "../invoice-card-shell";
import type { FormatInvoiceMoney } from "./invoice-money";
import {
  INVOICE_CARD_ACCENT_BAR_WIDTH,
  INVOICE_CARD_COLORS,
  INVOICE_CARD_RADIUS,
  type InvoicePayerKind,
  invoiceAccentColor,
} from "./tokens";

const SELF_PAY_PAYER_NAME = "Patient";
const MEMBERSHIP_PLACEHOLDER = "—";
const MEMBERSHIP_LABEL = "Membership no.";
const INSURANCE_NAME_LABEL = "Insurance name";
const PAYER_FIELD_WIDTH = 168;
const PAYER_FIELD_MIN_WIDTH = 132;
const PAYER_FIELD_MAX_WIDTH = 220;

type PayerFieldsLayout = "inline" | "stacked" | "none";

const SectionTitle = styled("div")<Theme>(({ theme }) => ({
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  marginBottom: 10,
}));

const PayerList = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const PayerCard = styled("div")<Theme, { "data-payer-kind": InvoicePayerKind }>(
  ({ theme, ...props }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${INVOICE_CARD_COLORS.border}`,
    borderLeft: `${INVOICE_CARD_ACCENT_BAR_WIDTH}px solid ${invoiceAccentColor(
      theme,
      props["data-payer-kind"],
    )}`,
    borderRadius: INVOICE_CARD_RADIUS.inner,
    padding: "10px 12px",
  }),
);

const PayerRow = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

/** Name over detail as one block, so neither can be spread by the row's height. */
const PayerText = styled("div")({
  flex: "0 1 auto",
  minWidth: 0,
});

const PayerName = styled("div")<Theme>(({ theme }) => ({
  fontWeight: 700,
  fontSize: 14,
  lineHeight: 1.35,
  color: theme.palette.text.primary,
}));

const PayerDetail = styled("div")<Theme, { "data-editable": boolean }>(
  ({ theme, ...props }) => ({
    fontWeight: 500,
    fontSize: props["data-editable"] ? 12 : 12.5,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
  }),
);

/**
 * Ordered after the fields so that the one flex line the row gets while there is
 * room reads name · fields · amount, and the auto margin holds the amount at the
 * right edge whether or not there are fields to sit before it. Once the fields
 * claim a full line of their own the orders tie, and the amount — earlier in the
 * DOM — stays up on the first line beside the name.
 */
const PayerAmount = styled("div")<
  Theme,
  { "data-payer-kind": InvoicePayerKind }
>(({ theme, ...props }) => ({
  order: 3,
  flex: "0 0 auto",
  marginLeft: "auto",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: 1.35,
  color: invoiceAccentColor(theme, props["data-payer-kind"]),
  whiteSpace: "nowrap",
}));

// Both branches have to declare the same properties: JSS updates the values of a
// prop-driven rule but never removes a property a later evaluation drops, so a
// one-sided `flex` would survive the switch and pin the basis at `auto`.
const PayerFields = styled("div")<Theme, { "data-fields": PayerFieldsLayout }>(
  ({ theme, ...props }) => ({
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minWidth: 0,
    // A basis of the full row is what drops the fields onto a line of their own
    // once the card is too narrow to seat them beside the name.
    ...(props["data-fields"] === "stacked"
      ? { order: 3, flex: "1 1 100%" }
      : { order: 2, flex: "0 1 auto" }),
  }),
);

const PayerFieldSlot = styled("div")<
  Theme,
  { "data-fields": PayerFieldsLayout }
>(({ ...props }) => ({
  flex:
    props["data-fields"] === "stacked"
      ? `1 1 ${PAYER_FIELD_MIN_WIDTH}px`
      : `0 1 ${PAYER_FIELD_WIDTH}px`,
  minWidth: PAYER_FIELD_MIN_WIDTH,
  maxWidth: PAYER_FIELD_MAX_WIDTH,
  "& input": { textAlign: "center" },
  // An empty field shows its label, not the input's placeholder, so the label is
  // what has to be centred for the field to read as centred. Scaling from the
  // centre holds the shrunk state on the same axis as the value.
  "& .MuiFormLabel-root": {
    marginLeft: 0,
    right: 0,
    textAlign: "center",
    transformOrigin: "top center",
  },
  // MUI's own dense rule outranks the theme's outlined one, and carries both a
  // 14px indent and a resting position meant for a shorter input than this
  // theme's. Restating the theme's value puts the label where its value lands.
  "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": {
    transform: "translate(0, 22px) scale(1)",
  },
}));

/**
 * Insurers are keyed by `id` so the draft can address their membership field;
 * the patient entry is singular and carries neither.
 */
export type InvoicePayerSummary =
  | {
      kind: "insurer";
      id: string;
      name: string;
      isPrivate: boolean;
      itemCount: number;
      amount: number;
      /** Read-only mode only — the draft reads the number back off the form. */
      membershipNumber?: string;
    }
  | {
      kind: "self-pay";
      itemCount: number;
      amount: number;
    };

const itemsLabel = (count: number): string =>
  `${count} ${count === 1 ? "item" : "items"}`;

const payerDetailLine = (
  payer: InvoicePayerSummary,
  editable: boolean,
): string => {
  const items = itemsLabel(payer.itemCount);

  if (payer.kind !== "insurer") return items;

  return editable
    ? `${items} on this tariff`
    : `${items} · membership ${payer.membershipNumber || MEMBERSHIP_PLACEHOLDER}`;
};

const orderPayers = (payers: InvoicePayerSummary[]): InvoicePayerSummary[] => [
  ...payers.filter((payer) => payer.kind === "insurer"),
  ...payers.filter((payer) => payer.kind === "self-pay"),
];

/**
 * The payers are derived from the tariffs picked on the lines — never edited
 * directly, so there is no add or remove affordance.
 */
export const PayersOnInvoice: FC<
  {
    payers: InvoicePayerSummary[];
    formatMoney: FormatInvoiceMoney;
  } & (
    | {
        mode: "editable";
        fieldNames: { memberships: string; insuranceNames: string };
      }
    | { mode: "read-only"; fieldNames?: never }
  ) &
    Omit<ComponentProps<typeof InvoiceCardSection>, "children">
> = ({ payers, formatMoney, mode, fieldNames, ...restProps }) => {
  const { isCompact } = useInvoiceCardLayout();
  const editable = mode === "editable";
  const editableFields: PayerFieldsLayout = isCompact ? "stacked" : "inline";

  return (
    <InvoiceCardSection
      tone="alt"
      paddingTop={editable ? 16 : 14}
      paddingBottom={editable ? 16 : 14}
      {...restProps}
    >
      <SectionTitle>Payers on this invoice</SectionTitle>
      <PayerList>
        {orderPayers(payers).map((payer) => {
          const fields: PayerFieldsLayout =
            editable && payer.kind === "insurer" ? editableFields : "none";

          return (
            <PayerCard
              key={payer.kind === "insurer" ? payer.id : payer.kind}
              data-payer-kind={payer.kind}
            >
              <PayerRow>
                <PayerText>
                  <PayerName>
                    {payer.kind === "insurer"
                      ? payer.name
                      : SELF_PAY_PAYER_NAME}
                  </PayerName>
                  <PayerDetail data-editable={editable}>
                    {payerDetailLine(payer, editable)}
                  </PayerDetail>
                </PayerText>
                <PayerAmount data-payer-kind={payer.kind}>
                  {formatMoney(payer.amount)}
                </PayerAmount>
                {mode === "editable" && payer.kind === "insurer" && (
                  <PayerFields data-fields={fields}>
                    {payer.isPrivate && (
                      <PayerFieldSlot data-fields={fields}>
                        <RHFTextField
                          name={`${fieldNames.insuranceNames}.${payer.id}`}
                          label={INSURANCE_NAME_LABEL}
                          size="small"
                          fullWidth
                          hideHelper
                        />
                      </PayerFieldSlot>
                    )}
                    <PayerFieldSlot data-fields={fields}>
                      <RHFTextField
                        name={`${fieldNames.memberships}.${payer.id}`}
                        label={MEMBERSHIP_LABEL}
                        size="small"
                        fullWidth
                        hideHelper
                      />
                    </PayerFieldSlot>
                  </PayerFields>
                )}
              </PayerRow>
            </PayerCard>
          );
        })}
      </PayerList>
    </InvoiceCardSection>
  );
};
